import type { ContentInsertType } from "../types"
import {
  DIAGRAM_DATA_ATTR,
  DIAGRAM_DISPLAY_STYLE,
  DIAGRAM_LIGHT_CANVAS_STYLE,
  diagramAltLabel,
  isDiagramContentType,
} from "./diagramConstants"
import { normalizeDiagramSvgDataUri } from "./diagramSvg"
import { decodeDiagramXml, encodeDiagramXml } from "./diagramXml"

export type DiagramSavePayload = {
  src: string
  diagramXml: string
  contentType: ContentInsertType
}

export type DiagramNodeAttrs = {
  src: string
  diagramXml: string
  contentType: ContentInsertType
  alt: string
  style: string
  width?: number | null
  height?: number | null
}

const styleDeclarations = (style: string) =>
  style
    .split(";")
    .map((declaration) => declaration.trim())
    .filter(Boolean)

export const ensureDiagramLightCanvasStyle = (
  style: string | null | undefined
): string => {
  const declarations = styleDeclarations(style?.trim() || DIAGRAM_DISPLAY_STYLE)
    .filter(
      (declaration) =>
        !/^(background-color|color-scheme)\s*:/i.test(declaration)
    )

  return [
    ...declarations,
    ...styleDeclarations(DIAGRAM_LIGHT_CANVAS_STYLE),
  ].join(";") + ";"
}

export const buildDiagramNodeAttrs = (
  payload: DiagramSavePayload
): DiagramNodeAttrs => ({
  src: normalizeDiagramSvgDataUri(payload.src),
  diagramXml: encodeDiagramXml(payload.diagramXml),
  contentType: payload.contentType,
  alt: diagramAltLabel(payload.contentType),
  style: ensureDiagramLightCanvasStyle(DIAGRAM_DISPLAY_STYLE),
})

export const diagramAttrsFromElement = (
  element: HTMLElement
): Partial<DiagramNodeAttrs> | null => {
  const contentType = element.getAttribute(DIAGRAM_DATA_ATTR.contentType)
  if (!isDiagramContentType(contentType)) return null

  const src = element.getAttribute("src")
  if (!src) return null

  const encodedXml = element.getAttribute(DIAGRAM_DATA_ATTR.diagramXml) ?? ""
  const widthAttr = element.getAttribute("width")
  const heightAttr = element.getAttribute("height")

  return {
    src: normalizeDiagramSvgDataUri(src),
    diagramXml: encodedXml,
    contentType,
    alt: element.getAttribute("alt") ?? diagramAltLabel(contentType),
    style: ensureDiagramLightCanvasStyle(element.getAttribute("style")),
    width: widthAttr ? Number(widthAttr) : null,
    height: heightAttr ? Number(heightAttr) : null,
  }
}

export const getEditableDiagramXml = (encodedXml: string | null | undefined) =>
  decodeDiagramXml(encodedXml ?? "")

export const renderDiagramHtmlAttributes = (attrs: DiagramNodeAttrs) => ({
  src: normalizeDiagramSvgDataUri(attrs.src),
  alt: attrs.alt,
  class: "tiptap-editor__diagram tiptap-editor__image",
  style: ensureDiagramLightCanvasStyle(attrs.style),
  [DIAGRAM_DATA_ATTR.contentType]: attrs.contentType,
  ...(attrs.diagramXml
    ? { [DIAGRAM_DATA_ATTR.diagramXml]: attrs.diagramXml }
    : {}),
  ...(attrs.width ? { width: attrs.width } : {}),
  ...(attrs.height ? { height: attrs.height } : {}),
})
